import { Form } from "antd";
import { useEffect, useState } from "react";
import {
  addCompanyIndustry,
  deleteCompanyIndustry,
  updateCompanyIndustry,
} from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import { CompanyIndustriesForm } from "./IndustriesView.default";
import { IndustryForm } from "./IndustryFormDialog.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { hasFormTouched } from "../utils/hasFormChanged";
import { setFormFields } from "../utils/setFormFields";
import { getProblemMessage } from "../utils/http";

export interface IndustryFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: CompanyIndustriesForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function IndustryFormDialog(props: IndustryFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<IndustryForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) {
        setFormFields(form, props.initialValues);
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { name, sysName, description } = values;
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "add") {
      addCompanyIndustry({
        name,
        sysName,
        description,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        // sysName is system-reserved — never sent on update.
        updateCompanyIndustry({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          name,
          description,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteCompanyIndustry(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setValid(false);
    setErrorMessage(undefined);
    setLoading(false);
    form.resetFields();
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Industry", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls:
                  props.mode === "add"
                    ? [
                        {
                          type: "textbox",
                          name: "name",
                          label: "Name",
                          rules: [
                            {
                              required: true,
                              message: "Name is required",
                            },
                          ],
                        },
                        {
                          type: "textbox",
                          name: "sysName",
                          label: "System Name",
                          rules: [
                            {
                              required: true,
                              message: "System Name is required",
                            },
                          ],
                        },
                        {
                          type: "textbox",
                          name: "description",
                          label: "Description",
                          rules: [
                            {
                              required: true,
                              message: "Description is required",
                            },
                          ],
                        },
                      ]
                    : [
                        {
                          type: "textbox",
                          name: "name",
                          label: "Name",
                          rules: [
                            {
                              required: true,
                              message: "Name is required",
                            },
                          ],
                        },
                        {
                          // System Name is system-reserved — shown read-only on edit.
                          type: "textbox",
                          name: "sysName",
                          label: "System Name",
                          readOnly: true,
                        },
                        {
                          type: "textbox",
                          name: "description",
                          label: "Description",
                          rules: [
                            {
                              required: true,
                              message: "Description is required",
                            },
                          ],
                        },
                      ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="industry"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default IndustryFormDialog;
